import React, { useState, useEffect, useRef } from 'react';
import { Star, MapPin, Award, Briefcase, Users, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

// Premium Trainer Ad Runner
export function TrainerAdRunner({ trainers = [] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const scrollRef = useRef(null);

  // Auto-scroll effect
  useEffect(() => {
    if (isHovered || trainers.length <= 6) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % Math.max(1, trainers.length - 5));
    }, 3000);

    return () => clearInterval(interval);
  }, [isHovered, trainers.length]);

  const scroll = (direction) => {
    if (direction === 'left') {
      setCurrentIndex((prev) => Math.max(0, prev - 1));
    } else {
      setCurrentIndex((prev) => Math.min(trainers.length - 6, prev + 1));
    }
  };

  if (!trainers || trainers.length === 0) {
    return null;
  }

  return (
    <div className="relative py-8 bg-gradient-to-r from-purple-50 via-white to-purple-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Sparkles className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-800">Featured Premium Trainers</h3>
              <p className="text-sm text-slate-500">Top-rated trainers with premium subscriptions</p>
            </div>
          </div>
          
          {trainers.length > 6 && (
            <div className="flex gap-2">
              <button
                onClick={() => scroll('left')}
                disabled={currentIndex === 0}
                className="p-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
              <button
                onClick={() => scroll('right')}
                disabled={currentIndex >= trainers.length - 6}
                className="p-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight className="w-5 h-5 text-slate-600" />
              </button>
            </div>
          )}
        </div>

        {/* Trainer Cards Carousel */}
        <div 
          className="overflow-hidden"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div 
            ref={scrollRef}
            className="flex gap-4 transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * (100 / 6 + 1)}%)` }}
          >
            {trainers.map((trainer, index) => (
              <div 
                key={trainer.id || index}
                className="flex-shrink-0 w-[calc(16.666%-14px)]"
              >
                <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-lg hover:border-purple-200 transition-all duration-300 h-full">
                  {/* Premium Badge */}
                  <div className="flex justify-between items-start mb-3">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                      {trainer.name?.charAt(0) || 'T'}
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-xs font-medium rounded-full">
                      <Award className="w-3 h-3" />
                      Premium
                    </span>
                  </div>

                  {/* Trainer Info */}
                  <h4 className="font-semibold text-slate-800 truncate">{trainer.name || 'Expert Trainer'}</h4>
                  
                  <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                    <MapPin className="w-3 h-3" />
                    <span className="truncate">{trainer.location || 'Remote'}</span>
                  </div>

                  {/* Rating */}
                  <div className="flex items-center gap-1 mt-2">
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                    <span className="font-medium text-slate-700">{trainer.rating?.toFixed(1) || '5.0'}</span>
                    <span className="text-slate-400 text-xs">({trainer.total_reviews || 0})</span>
                  </div>

                  {/* Skills */}
                  <div className="flex flex-wrap gap-1 mt-3">
                    {(trainer.skills || ['Leadership', 'Management']).slice(0, 2).map((skill, i) => (
                      <span key={i} className="px-2 py-0.5 bg-purple-50 text-purple-600 text-xs rounded-full">
                        {skill}
                      </span>
                    ))}
                  </div>

                  {/* Rate */}
                  {trainer.hourly_rate > 0 && (
                    <p className="text-sm font-semibold text-purple-600 mt-3">
                      ${trainer.hourly_rate}/hr
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination Dots */}
        {trainers.length > 6 && (
          <div className="flex justify-center gap-2 mt-4">
            {Array.from({ length: Math.ceil(trainers.length - 5) }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentIndex ? 'bg-purple-600 w-6' : 'bg-slate-300 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Premium Company Ad Runner
export function CompanyAdRunner({ companies = [] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Auto-scroll effect
  useEffect(() => {
    if (isHovered || companies.length <= 6) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % Math.max(1, companies.length - 5));
    }, 4000); // Slightly different timing for visual variety

    return () => clearInterval(interval);
  }, [isHovered, companies.length]);

  const scroll = (direction) => {
    if (direction === 'left') {
      setCurrentIndex((prev) => Math.max(0, prev - 1));
    } else {
      setCurrentIndex((prev) => Math.min(companies.length - 6, prev + 1));
    }
  };

  if (!companies || companies.length === 0) {
    return null;
  }

  return (
    <div className="relative py-8 bg-gradient-to-r from-blue-50 via-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Briefcase className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-800">Featured Premium Employers</h3>
              <p className="text-sm text-slate-500">Leading companies actively hiring trainers</p>
            </div>
          </div>
          
          {companies.length > 6 && (
            <div className="flex gap-2">
              <button
                onClick={() => scroll('left')}
                disabled={currentIndex === 0}
                className="p-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
              <button
                onClick={() => scroll('right')}
                disabled={currentIndex >= companies.length - 6}
                className="p-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight className="w-5 h-5 text-slate-600" />
              </button>
            </div>
          )}
        </div>

        {/* Company Cards Carousel */}
        <div 
          className="overflow-hidden"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div 
            className="flex gap-4 transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * (100 / 6 + 1)}%)` }}
          >
            {companies.map((company, index) => (
              <div 
                key={company.id || index}
                className="flex-shrink-0 w-[calc(16.666%-14px)]"
              >
                <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-lg hover:border-blue-200 transition-all duration-300 h-full">
                  {/* Premium Badge */}
                  <div className="flex justify-between items-start mb-3">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                      {company.name?.charAt(0) || 'C'}
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-medium rounded-full">
                      <Award className="w-3 h-3" />
                      Premium
                    </span>
                  </div>

                  {/* Company Info */}
                  <h4 className="font-semibold text-slate-800 truncate">{company.name || 'Premium Company'}</h4>
                  
                  <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                    <MapPin className="w-3 h-3" />
                    <span className="truncate">{company.location || 'Multiple Locations'}</span>
                  </div>

                  {/* Industry */}
                  <div className="flex items-center gap-1 mt-2 text-sm text-slate-600">
                    <Briefcase className="w-3 h-3" />
                    <span className="truncate">{company.industry || 'Technology'}</span>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {company.active_jobs || 0} Requirements
                    </span>
                  </div>

                  {/* View Jobs Button */}
                  <Link 
                    to={`/search?type=jobs&company=${company.id}`}
                    className="mt-3 block w-full py-2 text-center text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    View Requirements
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination Dots */}
        {companies.length > 6 && (
          <div className="flex justify-center gap-2 mt-4">
            {Array.from({ length: Math.ceil(companies.length - 5) }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentIndex ? 'bg-blue-600 w-6' : 'bg-slate-300 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default { TrainerAdRunner, CompanyAdRunner };
